import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { pb } from '../../../pocketbase'
import { COL_EMPLOYEES, COL_ROLES, COL_SHIFTS } from '../../../config/collections'
import type { EmployeeRecord, RoleRecord, ShiftRecord } from '../../../types/records'
import { isSupabaseAuth } from '../../../lib/supabase'
import { Alert, ButtonLink, FormField, PageHeader, PageShell, formInputProps } from '../../../components/ui'
import { btn, formActionsClass, formGridClass } from '../../../lib/ui'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'

export function EmployeeFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new' || !id

  const [roles, setRoles] = useState<RoleRecord[]>([])
  const [shifts, setShifts] = useState<ShiftRecord[]>([])
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [roleId, setRoleId] = useState('')
  const [assignedShiftId, setAssignedShiftId] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(!isNew)

  const loadRoles = useCallback(async () => {
    try {
      const res = await pb.collection(COL_ROLES).getFullList<RoleRecord>({ sort: 'name' })
      setRoles(res)
    } catch {
      setRoles([])
    }
  }, [])

  const loadShifts = useCallback(async () => {
    try {
      const res = await pb.collection(COL_SHIFTS).getFullList<ShiftRecord>({
        sort: 'sort,name',
        filter: 'active = true',
      })
      setShifts(res)
    } catch {
      setShifts([])
    }
  }, [])

  const loadEmployee = useCallback(async () => {
    if (!id || isNew) return
    setLoading(true)
    setErr('')
    try {
      const row = await pb.collection(COL_EMPLOYEES).getOne<EmployeeRecord>(id, {
        expand: 'role,assignedShift',
      })
      setEmail(row.email)
      setName(row.name || '')
      setRoleId(row.role || '')
      setAssignedShiftId(row.assignedShift || '')
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка'))
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void loadRoles()
    void loadShifts()
  }, [loadRoles, loadShifts])

  useEffect(() => {
    void loadEmployee()
  }, [loadEmployee])

  function roleNameFor(idVal: string): string {
    const r = roles.find((x) => x.id === idVal)
    return r?.name || 'viewer'
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr('')
    if (!roleId) {
      setErr('Выберите роль из справочника.')
      return
    }
    if (isNew) {
      if (!password || password.length < 8) {
        setErr('Пароль не короче 8 символов.')
        return
      }
      if (password !== passwordConfirm) {
        setErr('Пароли не совпадают.')
        return
      }
    }

    const roleName = roleNameFor(roleId)

    try {
      if (isNew) {
        await pb.collection(COL_EMPLOYEES).create({
          email: email.trim(),
          emailVisibility: true,
          password,
          passwordConfirm,
          name: name.trim(),
          role: roleId,
          roleName,
          assignedShift: assignedShiftId || '',
        })
      } else {
        const payload: Record<string, unknown> = {
          email: email.trim(),
          emailVisibility: true,
          name: name.trim(),
          role: roleId,
          roleName,
          assignedShift: assignedShiftId || '',
        }
        if (password) {
          if (password.length < 8) {
            setErr('Пароль не короче 8 символов.')
            return
          }
          if (password !== passwordConfirm) {
            setErr('Пароли не совпадают.')
            return
          }
          payload.password = password
          payload.passwordConfirm = passwordConfirm
        }
        await pb.collection(COL_EMPLOYEES).update(id!, payload)
      }
      navigate('/admin/administration/employees', { replace: true })
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    }
  }

  if (isSupabaseAuth()) {
    return (
      <PageShell>
        <PageHeader
          title="Пользователь"
          lead="Учётные записи хранятся во внешней CRM — создание и редактирование в этом интерфейсе недоступны."
        />
        <ButtonLink to="/admin/administration/employees" variant="primary">
          К списку пользователей
        </ButtonLink>
      </PageShell>
    )
  }

  if (roles.length === 0 && !loading) {
    return (
      <PageShell>
        <PageHeader title={isNew ? 'Новый пользователь' : 'Пользователь'} />
        <Alert kind="warn">
          Сначала создайте хотя бы одну роль на вкладке «Роли» (коллекция roles), затем вернитесь
          сюда.
        </Alert>
        <ButtonLink to="/admin/administration/roles" variant="primary">
          К ролям
        </ButtonLink>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHeader
        title={isNew ? 'Новый пользователь' : 'Редактирование'}
        lead="Поле «Системная роль» (roleName) синхронизируется с выбранной записью справочника ролей. Смена мастера задаёт фильтр графика по умолчанию."
      />
      {err && <Alert kind="err">{err}</Alert>}
      {loading && !isNew ? (
        <p>Загрузка…</p>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className={formGridClass}>
          <FormField label="Email" htmlFor="emp-email">
            <input
              id="emp-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Имя" htmlFor="emp-name">
            <input
              id="emp-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Роль (справочник)" htmlFor="emp-role">
            <select
              id="emp-role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              required
              {...formInputProps()}
            >
              <option value="">— выберите —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.title} ({r.name})
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Смена мастера (необязательно)" htmlFor="emp-shift">
            <select
              id="emp-shift"
              value={assignedShiftId}
              onChange={(e) => setAssignedShiftId(e.target.value)}
              {...formInputProps()}
            >
              <option value="">— не задана —</option>
              {shifts.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.startTime}–{s.endTime})
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={isNew ? 'Пароль' : 'Новый пароль (необязательно)'} htmlFor="emp-pass">
            <input
              id="emp-pass"
              type="password"
              autoComplete={isNew ? 'new-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={isNew}
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Подтверждение пароля" htmlFor="emp-pass2">
            <input
              id="emp-pass2"
              type="password"
              autoComplete={isNew ? 'new-password' : 'new-password'}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required={isNew || !!password}
              {...formInputProps()}
            />
          </FormField>
          <div className={formActionsClass}>
            <button type="submit" className={btn('primary')}>
              Сохранить
            </button>
            <ButtonLink to="/admin/administration/employees" variant="ghost">
              Отмена
            </ButtonLink>
          </div>
        </form>
      )}
    </PageShell>
  )
}
